import React, { useEffect, useState } from "react";
import {
  Alert, Button, Card, Col, DatePicker, Input, InputNumber,
  message, Modal, Row, Space, Table, Tag, Typography, Spin,
} from "antd";
import { SaveOutlined, ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

export default function FrmLecheBatch() {
  const [fecha, setFecha] = useState(dayjs());
  const [vacas, setVacas] = useState([]);         // lista de hembras activas
  const [valores, setValores] = useState({});      // { animalId: { litros, observacion } }
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null); // { tipo, titulo, lineas: [] }

  const cargarVacas = async () => {
    try {
      setCargando(true);
      const resp = await axios.get(`${API_URL}/Animal`, {
        params: { sexo: "Hembra", estado: "Activo", enProduccion: true, pagina: 1, tamañoPagina: 200 },
      });
      const lista = Array.isArray(resp.data?.lista?.$values)
        ? resp.data.lista.$values
        : Array.isArray(resp.data?.lista)
        ? resp.data.lista
        : [];
      setVacas(lista);

      // Inicializar valores en 0 para cada vaca
      const init = {};
      lista.forEach(v => { init[v.id] = { litros: null, observacion: "" }; });
      setValores(init);
    } catch {
      message.error("Error cargando lista de animales.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarVacas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLitros = (animalId, litros) => {
    setValores(prev => ({ ...prev, [animalId]: { ...prev[animalId], litros } }));
  };

  const setObservacion = (animalId, observacion) => {
    setValores(prev => ({ ...prev, [animalId]: { ...prev[animalId], observacion } }));
  };

  const onGuardar = async () => {
    console.log("▶ onGuardar iniciado");
    setResultado(null);

    const registros = vacas
      .filter(v => valores[v.id]?.litros != null && valores[v.id]?.litros > 0)
      .map(v => ({
        animalId: v.id,
        fecha: fecha.format("YYYY-MM-DD"),
        litrosTotal: valores[v.id].litros,
        observacion: valores[v.id].observacion || null,
      }));

    console.log("▶ registros a enviar:", registros);

    if (registros.length === 0) {
      setResultado({ tipo: "warning", titulo: "Sin datos", lineas: ["Ingrese litros para al menos una vaca."] });
      return;
    }

    try {
      setGuardando(true);
      console.log(`▶ POST ${API_URL}/Leche/lote`);
      const resp = await axios.post(`${API_URL}/Leche/lote`, registros);
      console.log("▶ Respuesta del servidor:", resp.data);

      const data = resp.data;
      const totalCreados = data?.creados ?? 0;
      // ReferenceHandler.Preserve envuelve arrays en { $id, $values }
      const erroresArr = Array.isArray(data?.errores?.$values)
        ? data.errores.$values
        : Array.isArray(data?.errores)
        ? data.errores
        : [];

      if (totalCreados > 0 && erroresArr.length === 0) {
        setResultado({
          tipo: "success",
          titulo: `✅ ${totalCreados} registro(s) guardados correctamente`,
          lineas: registros.map(r => `Animal ${r.animalId}: ${r.litrosTotal} L`),
        });
        // Limpiar formulario
        const reset = {};
        vacas.forEach(v => { reset[v.id] = { litros: null, observacion: "" }; });
        setValores(reset);
      } else if (totalCreados > 0 && erroresArr.length > 0) {
        setResultado({
          tipo: "warning",
          titulo: `⚠️ ${totalCreados} guardados, ${erroresArr.length} con error`,
          lineas: erroresArr,
        });
        const reset = {};
        vacas.forEach(v => { reset[v.id] = { litros: null, observacion: "" }; });
        setValores(reset);
      } else {
        setResultado({
          tipo: "error",
          titulo: "❌ No se guardó ningún registro",
          lineas: erroresArr.length > 0
            ? erroresArr
            : ["Verifique que el backend esté corriendo y la fecha no tenga registros duplicados."],
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      const detalle = err?.response?.data?.detalle || err?.response?.data?.message || err?.message || "Error desconocido";
      console.error("❌ Error Leche/lote:", status, err?.response?.data ?? err);
      setResultado({
        tipo: "error",
        titulo: `❌ Error ${status ?? "de red"} al guardar`,
        lineas: [detalle, `URL: ${API_URL}/Leche/lote`, `Estado HTTP: ${status ?? "sin respuesta"}`],
      });
    } finally {
      setGuardando(false);
    }
  };

  const totalLitros = vacas.reduce((sum, v) => sum + (valores[v.id]?.litros ?? 0), 0);
  const conDatos = vacas.filter(v => (valores[v.id]?.litros ?? 0) > 0).length;

  const columns = [
    {
      title: "Arete",
      dataIndex: "arete",
      key: "arete",
      width: 90,
    },
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
    },
    {
      title: "Raza",
      dataIndex: "razaNombre",
      key: "razaNombre",
      responsive: ["md"],
      render: v => v || "—",
    },
    {
      title: "Etapa",
      dataIndex: "etapaProductivaCache",
      key: "etapa",
      responsive: ["md"],
      render: v => v ? <Tag>{v}</Tag> : "—",
    },
    {
      title: "Litros del ordeño ✏️",
      key: "litros",
      width: 180,
      render: (_, record) => {
        const val = valores[record.id]?.litros;
        return (
          <InputNumber
            min={0}
            step={0.5}
            precision={2}
            style={{
              width: "100%",
              background: val > 0 ? "#f6ffed" : undefined,
              borderColor: val > 0 ? "#52c41a" : undefined,
            }}
            placeholder="Ej: 8.50"
            value={val ?? null}
            onChange={v => setLitros(record.id, v)}
          />
        );
      },
    },
    {
      title: "Observación",
      key: "obs",
      render: (_, record) => (
        <Input
          placeholder="Ej: produjo menos, enfermedad..."
          maxLength={100}
          value={valores[record.id]?.observacion ?? ""}
          onChange={e => setObservacion(record.id, e.target.value)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>Registro de Leche por Lote</Title>
      <Text type="secondary">
        Escriba los litros producidos en la columna <strong>"Litros del ordeño"</strong> para cada vaca.
        Las celdas se ponen en verde cuando tienen un valor. Al terminar presione <strong>Guardar</strong>.
        Las vacas con campo vacío o en 0 se omiten.
      </Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Fecha del ordeño</Text>
              <DatePicker
                style={{ width: "100%" }}
                value={fecha}
                onChange={val => setFecha(val)}
                format="YYYY-MM-DD"
              />
            </Space>
          </Col>
          <Col xs={12} md={4}>
            <Card size="small" style={{ textAlign: "center" }}>
              <Text type="secondary">Vacas con datos</Text>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{conDatos}</div>
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card size="small" style={{ textAlign: "center" }}>
              <Text type="secondary">Total litros</Text>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{totalLitros.toFixed(2)}</div>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Space wrap>
              <Button
                icon={<ReloadOutlined />}
                onClick={cargarVacas}
                loading={cargando}
              >
                Recargar vacas
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={onGuardar}
                loading={guardando}
                disabled={conDatos === 0}
              >
                Guardar {conDatos > 0 ? `(${conDatos})` : ""}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ── Resultado visible ── */}
      {resultado && (
        <Alert
          type={resultado.tipo}
          message={resultado.titulo}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {resultado.lineas.map((l, i) => <li key={i}>{l}</li>)}
            </ul>
          }
          showIcon
          closable
          onClose={() => setResultado(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={cargando}>
        {vacas.length === 0 && !cargando ? (
          <Card>
            <Text type="secondary">No hay hembras activas registradas.</Text>
          </Card>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={vacas}
            pagination={false}
            scroll={{ x: "max-content" }}
            size="middle"
          />
        )}
      </Spin>
    </div>
  );
}
